import { useState } from 'react';
import { getFunnelSessionCode } from '../../../utils/session';

interface SystemConfirmationProps {
  onNext: () => void;
}

export default function SystemConfirmation({ onNext }: SystemConfirmationProps) {
  const [confirmations, setConfirmations] = useState({
    notAgency: false,
    metaInfrastructure: false,
    budgetUpfront: false,
    paymentMethod: false,
    meetingRequest: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const allChecked = Object.values(confirmations).every(v => v);

  const handleToggle = (key: keyof typeof confirmations) => {
    setConfirmations({
      ...confirmations,
      [key]: !confirmations[key]
    });
  };

  const handleSubmit = async () => {
    if (!allChecked || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/funnel_kaydet.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          step: 'step4',
          oturum_kodu: getFunnelSessionCode(),
          onay_sistem_farkli: confirmations.notAgency,
          onay_meta_altyapisi: confirmations.metaInfrastructure,
          onay_toplam_butce: confirmations.budgetUpfront,
          onay_odeme_yontemi: confirmations.paymentMethod,
          onay_gorusme_talebi: confirmations.meetingRequest
        })
      });

      const result = await response.json();

      if (result.status === 'success') {
        onNext();
      } else {
        alert('Onay bilgileri kaydedilemedi. Lütfen tekrar deneyin.');
      }
    } catch (error) {
      console.error('Step 4 kayıt hatası:', error);
      alert('Sunucu bağlantı hatası oluştu.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const items = [
    {
      key: 'notAgency' as const,
      text: 'Smart Hizmet sisteminin klasik reklam ajansı olmadığını anladım'
    },
    {
      key: 'metaInfrastructure' as const,
      text: 'Reklamların Smart Hizmet Meta altyapısından yürütüleceğini ve kendi Meta reklam hesabımın kullanılmayacağını anladım'
    },
    {
      key: 'budgetUpfront' as const,
      text: 'Reklam bütçesinin günlük değil toplam bütçe olarak baştan yüklenmesi gerektiğini anladım'
    },
    {
      key: 'paymentMethod' as const,
      text: 'Ödemelerin kredi kartı veya IBAN ile yapılacağını anladım'
    },
    {
      key: 'meetingRequest' as const,
      text: 'Sistem yöneticisinin işime uygun plan hazırlaması için görüşme talep ediyorum'
    }
  ];

  return (
    <section className="min-h-screen flex items-center justify-center py-32 px-4 sm:px-6 lg:px-8 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900 to-black"></div>

      <div className="relative z-10 max-w-4xl mx-auto w-full">
        <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-500/30 rounded-2xl p-8 sm:p-12 shadow-2xl shadow-yellow-500/10">
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full mb-4">
              <i className="ri-shield-check-line text-2xl text-black"></i>
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Sistem Mantığını Onaylama
            </h2>

            <p className="text-gray-400">
              Devam etmeden önce lütfen aşağıdaki maddeleri okuyun ve onaylayın
            </p>
          </div>

          <div className="space-y-4 mb-8">
            {items.map((item) => (
              <div
                key={item.key}
                className={`flex items-start gap-4 p-6 rounded-xl cursor-pointer transition-all duration-300 border ${
                  confirmations[item.key]
                    ? 'bg-yellow-500/10 border-yellow-500/50'
                    : 'bg-black/30 border-white/10 hover:border-white/20'
                }`}
                onClick={() => handleToggle(item.key)}
              >
                <div
                  className={`flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-300 ${
                    confirmations[item.key]
                      ? 'bg-gradient-to-br from-yellow-500 to-yellow-600 border-yellow-500'
                      : 'border-white/30 bg-black/30'
                  }`}
                >
                  {confirmations[item.key] && (
                    <i className="ri-check-line text-black text-lg font-bold"></i>
                  )}
                </div>

                <div className="flex-1">
                  <p className="text-white font-medium leading-relaxed">
                    {item.text}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {!allChecked && (
            <div className="mb-6 flex items-center gap-2 text-yellow-500 text-sm bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
              <i className="ri-information-line text-xl"></i>
              <span>Devam etmek için lütfen tüm maddeleri okuyun ve onaylayın</span>
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={!allChecked || isSubmitting}
            className={`w-full py-4 rounded-lg font-bold text-lg transition-all duration-300 whitespace-nowrap ${
              allChecked && !isSubmitting
                ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-black hover:shadow-2xl hover:shadow-yellow-500/50 hover:scale-105 cursor-pointer'
                : 'bg-gray-700 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isSubmitting
              ? 'Kaydediliyor...'
              : allChecked
                ? 'Yönetici ile Görüşme Talebi Gönder'
                : 'Tüm Maddeleri Onaylayın'}
          </button>
        </div>
      </div>
    </section>
  );
}